import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND_ACCENT, BRAND_PLAIN, NAV_LINKS } from "./data";
import { useSmoothScroll } from "../../App";
import { EASE } from "./Reveal";

export default function Nav() {
  const [open, setOpen] = useState(false);
  const scrollTo = useSmoothScroll();

  const go = (href) => {
    setOpen(false);
    scrollTo(href);
  };

  return (
    <>
      <header
        data-testid="site-nav"
        className="fixed inset-x-0 top-0 z-50 border-b border-ivory/10 bg-forest/95 backdrop-blur-md"
      >
        <div className="mx-auto flex max-w-editorial items-center justify-between px-6 py-4 md:px-10">
          <button
            data-testid="nav-brand"
            onClick={() => go("#top")}
            className="font-serif text-lg leading-none text-ivory"
          >
            {BRAND_PLAIN} <em className="text-saffron">{BRAND_ACCENT}</em>
          </button>

          <nav className="hidden items-center gap-9 lg:flex" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                onClick={() => go(l.href)}
                className="text-[11px] font-medium uppercase tracking-[0.24em] text-ivory/70 transition-colors duration-300 hover:text-saffron"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-enquire-button"
              onClick={() => go("#enquire")}
              className="border border-saffron px-5 py-2.5 text-[11px] font-medium uppercase tracking-[0.24em] text-saffron transition-colors duration-300 hover:bg-saffron hover:text-forest"
            >
              Request Quote
            </button>
          </nav>

          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            className="text-ivory lg:hidden"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="fixed inset-0 z-40 bg-forest lg:hidden"
          >
            <div className="flex h-full flex-col justify-center gap-2 px-8">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.6, ease: EASE }}
                  onClick={() => go(l.href)}
                  className="py-3 text-left font-serif text-4xl font-light text-ivory"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                data-testid="nav-mobile-enquire-button"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6, ease: EASE }}
                onClick={() => go("#enquire")}
                className="mt-6 w-fit border border-saffron px-7 py-3.5 text-xs font-medium uppercase tracking-[0.24em] text-saffron"
              >
                Request Quote
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
